import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    TextField,
} from '../../components';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { IllustrationScribblingMiniature } from '../../icons';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { pad } from '../../../../Common/Utils';

export const page2FeelGoodListKey = 'page2FeelGoodList';
const page2FeelGoodListPlaceholders = [
    'F.eks. “gå en tur”',
    'F.eks. “ringe til en ven”',
    'F.eks. “høre musik”',
];

export const exerciseData33: ExerciseData = {
    ExerciseView: Module3Exercise3,
    dataKeys: [page2FeelGoodListKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Din kærlige huskeliste',
            description:
                'På din liste kan der stå aktiviteter, der plejer at få dig til at få det rart. Tænk over aktiviteter, hvor du ikke skal præstere, og hvor du kan være dig selv.',
            dataKeys: [page2FeelGoodListKey],
            isArray: true,
        }),
    ],
};

function Module3Exercise3({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Kærlig huskeliste fra mig til mig">
            <ProgressBar current={exercise.CurrentPage} total={3} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            savedData={JSON.parse(
                                exercise.Data[page2FeelGoodListKey] ?? '[]',
                            )}
                            onContinue={(data) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [page2FeelGoodListKey]: JSON.stringify(
                                            data
                                                .map(normalizeInput)
                                                .filter(Boolean),
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '3.3.completed',
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type PageProps = {
    onContinue: () => void;
};

function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Din liste over selvomsorg</LabelDisplay2>
            <LabelBody className="my-10">
                Når presset stiger og det hele spidser til, så er det nemt at
                droppe det allervigtigste: dig selv. Men netop i de perioder kan
                selvomsorg være dit stærkeste redskab. For at drage omsorg for
                sig selv kan virke beroligende og være en måde at komme ud af
                hovedet og ned i kroppen.
            </LabelBody>
            <LabelBody>
                I øvelsen skal du lave en liste med de ting, du godt kan lide at
                lave. Så bliver du klar på, hvad du kan gøre for at tanke op og
                passe på dig selv, når overskuddet er småt.
            </LabelBody>
            <SwirlyBox className="my-30">
                Når du er klar til at lave din liste, kan du gå videre.
            </SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Gå i gang</SecondaryButton>
        </>
    );
}

type Page2Data = string[];
type Page2Props = {
    savedData: Page2Data;
    onContinue: (data: Page2Data) => void;
};

function Page2({ savedData, onContinue }: Page2Props) {
    const [texts, setTexts] = useSyncedState(pad(savedData, 3, ''));

    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1 className="mb-10">
                    Din kærlige huskeliste
                </LabelDisplay1>
                <LabelBody>
                    På din liste kan der stå aktiviteter, der plejer at få dig
                    til at få det rart. Tænk over aktiviteter, hvor du ikke skal
                    præstere, og hvor du kan være dig selv.
                </LabelBody>
                {texts.map((x, idx) => (
                    <TextField
                        key={idx}
                        className="mt-30"
                        placeholder={page2FeelGoodListPlaceholders[idx]}
                        text={x}
                        onChangeText={(e) => {
                            const copy = texts.slice();
                            copy[idx] = e;
                            setTexts(copy);
                        }}
                    />
                ))}
                <GhostButton
                    className="my-30"
                    onClick={() => {
                        setTexts(texts.concat(''));
                    }}
                >
                    Tilføj mere
                </GhostButton>
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue(texts);
                }}
            >
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

function Page3({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay1 className="mb-10">Afrunding</LabelDisplay1>
            <LabelBody>
                En konkret, kærlig huskeliste kan gøre det nemmere at huske at
                få prioriteret selvomsorg i de svære øjeblikke. Udover at du kan
                finde din liste i logbogen, kan der være mange andre måder, du
                kan huske dig selv på det. Nogen sætter en reminder på sin
                telefon, andre hænger en post-it på spejlet. Det vigtigste er,
                at du husker at tage dig af dig selv.
            </LabelBody>
            <IllustrationScribblingMiniature className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
